<template>
  <div class="challenges-section">
    <div class="card">
      <div class="card-header">
        <div class="card-title">
          <i class="fa-solid fa-handshake"></i>
          <h2>Challenge Requests</h2>
          <span v-if="challenges.length > 0" class="badge-count">{{ challenges.length }}</span>
        </div>
        <button v-if="challenges.length > 0" class="btn-text" @click="$emit('refresh')">
          <i class="fa-solid fa-sync-alt"></i>
          Refresh
        </button>
      </div>
      <div class="card-body">
        <div v-if="loading" class="loading-state">
          <div class="spinner-small"></div>
          <p>Loading challenges...</p>
        </div>
        <div v-else-if="challenges.length > 0" class="challenges-list">
          <transition-group name="challenge-list">
            <div 
              v-for="challenge in challenges" 
              :key="challenge.id"
              class="challenge-card booking-card"
            >
              <div class="booking-header">
                <div class="booking-id">
                  <i class="fa-solid fa-bell"></i>
                  <span>Challenge #{{ challenge.id }}</span>
                </div>
                <div class="booking-meta">
                  <span class="booking-date">
                    <i class="fa-solid fa-clock"></i>
                    {{ formatDate(challengeDate(challenge)) }}
                  </span>
                  <span class="booking-status pending">
                    Awaiting your response
                  </span>
                </div>
              </div>
              <div class="booking-body">
                <div class="challenge-info">
                  <div class="challenger-team">
                    <div class="team-avatar">
                      <img
                        v-if="challenge.challenger_team_logo"
                        :src="challenge.challenger_team_logo"
                        :alt="challenge.challenger_team_name"
                        loading="lazy"
                        decoding="async"
                        @error="$emit('image-error', $event)"
                      >
                      <div v-else class="avatar-placeholder">
                        <i class="fa-solid fa-users"></i>
                      </div>
                    </div>
                    <div class="team-details">
                      <div class="team-name">{{ challenge.challenger_team_name }}</div>
                      <div class="challenge-label">wants to challenge your team</div>
                    </div>
                  </div>
                  <div class="challenge-details">
                    <div v-if="challengeDate(challenge)" class="detail-item">
                      <i class="fa-solid fa-calendar-day"></i>
                      <span>
                        {{ formatDate(challengeDate(challenge)) }}
                        at {{ formatTime(challengeDate(challenge)) }}
                      </span>
                    </div>
                    <div class="detail-item">
                      <i class="fa-solid fa-hourglass-half"></i>
                      <span>{{ challengeDuration(challenge) }} minutes</span>
                    </div>
                    <div class="detail-item">
                      <i class="fa-solid fa-users" aria-hidden="true"></i>
                      <span>{{ formatMatchFormat(challenge) }}</span>
                    </div>
                    <div v-if="challenge.match_type" class="detail-item">
                      <i class="fa-solid fa-trophy"></i>
                      <span>{{ formatMatchType(challenge.match_type) }}</span>
                    </div>
                    <div v-if="venueLabel(challenge)" class="detail-item detail-item--venue">
                      <i class="fa-solid fa-map-marker-alt"></i>
                      <div class="venue-info">
                        <span class="venue-text">{{ venueLabel(challenge) }}</span>
                        <a
                          v-if="venuePinUrl(challenge)"
                          :href="venuePinUrl(challenge)"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="venue-location-link"
                          @click.stop
                        >
                          <i class="fa-solid fa-map-location-dot"></i>
                          View pinned location
                        </a>
                      </div>
                    </div>
                    <div v-if="challenge.description" class="detail-item">
                      <i class="fa-solid fa-align-left"></i>
                      <span>{{ challenge.description }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="booking-actions challenge-actions">
                <button 
                  class="btn-action btn-success" 
                  @click.stop="$emit('accept', challenge.id)"
                  :disabled="processingChallenge === challenge.id"
                >
                  <i class="fa-solid fa-check"></i>
                  <span v-if="processingChallenge !== challenge.id">Accept</span>
                  <span v-else>Processing...</span>
                </button>
                <button 
                  class="btn-action btn-danger" 
                  @click.stop="$emit('reject', challenge.id)"
                  :disabled="processingChallenge === challenge.id"
                >
                  <i class="fa-solid fa-times"></i>
                  <span v-if="processingChallenge !== challenge.id">Reject</span>
                  <span v-else>Processing...</span>
                </button>
                <button 
                  class="btn-action btn-secondary" 
                  @click.stop="$emit('view-details', challenge.id)"
                >
                  <i class="fa-solid fa-eye"></i>
                  Details
                </button>
              </div>
            </div>
          </transition-group>
        </div>
        <div v-else class="empty-state">
          <div class="empty-icon">
            <i class="fa-solid fa-inbox"></i>
          </div>
          <h3>No Challenge Requests</h3>
          <p>You don't have any pending challenge requests at the moment.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ChallengesSection',
  props: {
    challenges: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    processingChallenge: {
      type: [Number, String],
      default: null
    },
    formatDate: {
      type: Function,
      required: true
    },
    formatTime: {
      type: Function,
      required: true
    }
  },
  emits: ['refresh', 'accept', 'reject', 'view-details', 'image-error'],
  methods: {
    challengeDate(challenge) {
      return challenge?.match_date || challenge?.scheduled_at || challenge?.date || null;
    },
    challengeDuration(challenge) {
      const d = challenge?.duration_minutes ?? challenge?.duration;
      return Number.isFinite(d) && d > 0 ? d : 90;
    },
    venueLabel(challenge) {
      const v = challenge?.venue;
      const name = v?.name || challenge?.venue_name;
      if (!name) return '';
      const city = v?.city || challenge?.venue_city;
      return city ? `${name}, ${city}` : name;
    },
    venuePinUrl(challenge) {
      const pin =
        challenge?.venue?.notes ||
        challenge?.pin_location ||
        challenge?.venue?.pin_location ||
        null;
      if (!pin || typeof pin !== 'string') return null;
      const trimmed = pin.trim();
      if (!trimmed) return null;
      if (/^https?:\/\//i.test(trimmed)) return trimmed;
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(trimmed)}`;
    },
    formatMatchFormat(challenge) {
      const format = challenge?.match_format;
      if (!format) return 'Format TBD';
      const labels = {
        '5v5': '5v5',
        '3v3': '3v3',
        full_court: 'Full court',
        half_court: 'Half court',
        singles: 'Singles',
        doubles: 'Doubles',
        '7s': 'Rugby 7s',
        '10s': 'Rugby 10s',
        '15s': 'Rugby 15s',
        '6v6': '6v6',
        beach: 'Beach',
      };
      return labels[format] || String(format).replace(/_/g, ' ');
    },
    formatMatchType(type) {
      if (!type) return '';
      return String(type).charAt(0).toUpperCase() + String(type).slice(1);
    },
  },
};
</script>

<style scoped>
.challenges-section {
  margin-top: 30px;
  margin-bottom: 30px;
}

.card {
  background: var(--card-bg);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 25px 30px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.card-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.card-title i {
  font-size: 1.5rem;
  color: var(--section-icon);
}

.card-title h2 {
  margin: 0;
  color: var(--heading-color);
  font-size: 1.5rem;
}

.badge-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 8px;
  background: linear-gradient(135deg, var(--border-color-hover), #4a9eff);
  color: white;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 700;
  margin-left: 8px;
}

.btn-text {
  background: none;
  border: none;
  color: var(--section-icon);
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.btn-text:hover {
  color: var(--heading-color);
  transform: translateX(5px);
}

.card-body {
  padding: 30px;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  gap: 16px;
}

.spinner-small {
  width: 32px;
  height: 32px;
  border: 3px solid var(--background-color3);
  border-top-color: var(--border-color-hover);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.challenges-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.booking-card {
  background: var(--card-bg);
  border-radius: 16px;
  padding: 20px;
  border: 2px solid transparent;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.challenge-card {
  border-left: 4px solid var(--border-color-hover);
  transition: all 0.3s ease;
}

.challenge-card:hover {
  transform: translateX(4px);
  box-shadow: 0 4px 16px rgba(58, 134, 255, 0.2);
}

.booking-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.booking-id {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  color: var(--heading-color);
}

.booking-meta {
  display: flex;
  align-items: center;
  gap: 15px;
}

.booking-date {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.85rem;
  color: var(--subtext-color);
}

.booking-status {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.booking-status.pending { 
  background: var(--pending-bg); 
  color: var(--pending-color);
  border: 1px solid var(--pending-border);
}

.booking-body {
  margin-bottom: 15px;
}

.challenge-info {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.challenger-team {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: var(--background-color2);
  border-radius: 12px;
}

.team-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  background: var(--background-color3);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 2px solid var(--border-color-hover);
}

.team-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-color2);
  font-size: 20px;
}

.team-details {
  flex: 1;
}

.team-name {
  font-weight: 700;
  font-size: 16px;
  color: var(--text-color1);
  margin-bottom: 4px;
}

.challenge-label {
  font-size: 13px;
  color: var(--text-color2);
  font-style: italic;
}

.challenge-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: var(--background-color2);
  border-radius: 12px;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: var(--text-color2);
}

.detail-item i {
  color: var(--border-color-hover);
  width: 16px;
  text-align: center;
  flex-shrink: 0;
}

.detail-item--venue {
  align-items: flex-start;
}

.venue-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.venue-text {
  line-height: 1.4;
}

.venue-location-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--border-color-hover);
  text-decoration: none;
}

.venue-location-link:hover {
  text-decoration: underline;
  color: var(--heading-color);
}

.booking-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.challenge-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.btn-action {
  flex: 1;
  padding: 8px 12px;
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.3s ease;
  min-width: 100px;
}

.btn-action:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}

.btn-action:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-success {
  background: linear-gradient(135deg, #4CAF50, #45a049);
  color: white;
  border: none;
}

.btn-success:hover:not(:disabled) {
  background: linear-gradient(135deg, #45a049, #3d8b40);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.4);
}

.btn-danger {
  background: linear-gradient(135deg, #f44336, #d32f2f);
  color: white;
  border: none;
}

.btn-danger:hover:not(:disabled) {
  background: linear-gradient(135deg, #d32f2f, #c62828);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(244, 67, 54, 0.4);
}

.btn-secondary {
  background: rgba(255, 255, 255, 0.1);
  color: var(--subtext-color);
  border: 1px solid var(--border-color-hover);
}

.btn-secondary:hover {
  background: rgba(255, 255, 255, 0.2);
  color: var(--heading-color);
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.empty-icon {
  width: 100px;
  height: 100px;
  margin: 0 auto 20px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  color: var(--subtext-color);
}

.empty-state h3 {
  color: var(--heading-color);
  margin: 0 0 10px 0;
}

.empty-state p {
  color: var(--subtext-color);
  margin-bottom: 20px;
}

.challenge-list-enter-active,
.challenge-list-leave-active {
  transition: all 0.4s ease;
}

.challenge-list-enter-from {
  opacity: 0;
  transform: translateY(-20px);
}

.challenge-list-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.challenge-list-move {
  transition: transform 0.4s ease;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 768px) {
  .challenge-actions {
    flex-direction: column;
  }
  
  .challenge-actions .btn-action {
    width: 100%;
  }
  
  .challenger-team {
    flex-direction: column;
    text-align: center;
  }
}
</style>
